<template>
  <div class="hyf-auth">

    <!-- NAV -->
    <nav class="auth-nav">
      <div class="auth-nav-inner">
        <a href="/" class="auth-brand">
          <img class="auth-brand-logo auth-brand-logo-desktop" :src="logoHorizontal" alt="House &amp; Flats">
          <img class="auth-brand-logo auth-brand-logo-mobile" :src="logoMark" alt="H&amp;F">
        </a>
        <div class="auth-nav-right">
          <!-- En las pantallas de recuperación la salida útil es volver a
               entrar, no registrarse: quien está acá ya tiene cuenta. -->
          <router-link v-if="isRegister || isRecover || isNewPassword" :to="{ name: 'login' }" class="auth-btn-secondary">{{ $t('login.sign-in') }}</router-link>
          <router-link v-else :to="{ name: 'register', params: { step: 1 } }" class="auth-btn-secondary">{{ $t('login.register') }}</router-link>
        </div>
      </div>
    </nav>

    <!-- MAIN CONTENT -->
    <!-- Login y registro: mismo diseño que Mi cuenta (lateral con título a la
         izquierda, tarjetas a la derecha; bkCuenta.css + bkAuth.css).
         Recuperar / cambiar clave siguen con el diseño anterior, centrado. -->
    <main :class="['auth-main', { 'auth-main--bk': isLogin || isRegister }]">
        <transition name="auth-fade" mode="out-in">
          <div v-if="isRegister" key="register" class="bk bk-auth">
            <aside class="bk-side">
              <h1 class="bk-title">{{ $t('auth_nuevo.reg.title_a') }} <span class="bk-grad">{{ $t('auth_nuevo.reg.title_b') }}</span></h1>
              <p class="bk-subtitle">{{ $t('auth_nuevo.reg.subtitle') }}</p>
              <Progress :step="step" />
            </aside>
            <div class="bk-main">
              <transition name="auth-step" mode="out-in">
                <div :key="step" class="auth-step-wrapper">
                  <Step1 v-if="step == 1" @next-step="nextStep" />
                  <Step2 v-if="step == 2" @next-step="nextStep" />
                  <Step3 v-if="step == 3" @next-step="nextStep" @registration-complete="onRegistrationComplete" />
                </div>
              </transition>
            </div>
          </div>
          <!-- Login: una sola columna, título arriba de la tarjeta. -->
          <div v-else-if="isLogin" key="login" class="bk bk-auth bk-auth--solo">
            <header class="bk-auth-head">
              <h1 class="bk-title">{{ $t('auth_nuevo.login.title_a') }} <span class="bk-grad">{{ $t('auth_nuevo.login.title_b') }}</span></h1>
              <p class="bk-subtitle">{{ $t('auth_nuevo.login.subtitle') }}</p>
            </header>
            <div class="bk-main">
              <Login />
            </div>
          </div>
          <div v-else-if="isRecover" key="recuperar" class="auth-container auth-view">
            <h1 class="auth-title">{{ message }}</h1>
            <Recuperar />
          </div>
          <div v-else-if="isNewPassword" key="cambiar-clave" class="auth-container auth-view">
            <h1 class="auth-title">{{ message }}</h1>
            <CambiarClave />
          </div>
        </transition>
    </main>

    <!-- FOOTER -->
    <footer class="auth-footer">
      <div class="auth-footer-inner">
        <div class="auth-footer-brand">
          <a href="/" class="auth-brand">
            <img class="auth-brand-logo" :src="logoHorizontal" alt="House &amp; Flats">
          </a>
          <p>Sistema de cierre para alojamiento internacional. Te ayudamos a asegurar una opción real antes de viajar.</p>
          <div class="auth-footer-social">
            <a class="socialcontainer" href="https://www.instagram.com/houseandflats?igsh=MWxza2RhbGVtOGhoYg==" target="_blank" rel="noopener noreferrer">
              <div class="icon social-icon-2-2">
                <svg fill="currentColor" viewBox="0 0 448 512" height="1.5em" xmlns="http://www.w3.org/2000/svg"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg>
              </div>
              <div class="social-icon-2">
                <svg fill="white" viewBox="0 0 448 512" height="1.5em" xmlns="http://www.w3.org/2000/svg"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg>
              </div>
            </a>
          </div>
        </div>
        <div class="auth-footer-col">
          <h5>Producto</h5>
          <ul>
            <li><a href="/">Inicio</a></li>
            <li><router-link :to="{ name: 'formulario', query: { page: 1 } }">Recibir mi selección</router-link></li>
          </ul>
        </div>
        <div class="auth-footer-col">
          <h5>Para empresas</h5>
          <ul>
            <li><a href="/">Partners</a></li>
            <li><a href="/">Universidades</a></li>
            <li><a href="/">Anfitriones</a></li>
          </ul>
        </div>
        <div class="auth-footer-col">
          <h5>Contacto</h5>
          <ul>
            <li><a href="https://houseandflats.com/blog/" target="_blank" rel="noopener">Blog</a></li>
            <li><a href="mailto:hello@houseandflats.com">hello@houseandflats.com</a></li>
            <li><a href="tel:+13055100028">+1 (305) 510-0028</a></li>
          </ul>
        </div>
      </div>
      <div class="auth-footer-bottom">
        <div>© 2026 House and Flats. Todos los derechos reservados.</div>
        <div class="auth-footer-legal">
          <a href="/tyc">Términos</a>
          <span>·</span>
          <a href="/privacidad">Privacidad</a>
          <span>·</span>
          <a href="#cookies">Cookies</a>
        </div>
      </div>
    </footer>

    <!-- Welcome Modal (hijo directo de .hyf-auth para evitar problemas de z-index) -->
    <transition name="welcome-modal">
      <div v-if="showWelcomeModal" class="welcome-overlay">
        <div class="welcome-card">
          <img :src="logoHorizontal" class="welcome-logo" alt="House&amp;Flats" />
          <h2 class="welcome-title">Bienvenido<span class="welcome-comma">,</span></h2>
          <p class="welcome-name">{{ welcomeName }}</p>
          <div class="welcome-divider"></div>
          <p class="welcome-redirect">Redirigiendo en unos segundos…</p>
          <div class="welcome-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
// Logo naranja de marca. El SVG no trae margen: anchos ~85 % de los del PNG negro.
import logoHorizontal from '@/new-assets/logo-horizontal.svg'
import logoMark from '@/assets/hyf-logo-mark-orange.png'

import Progress from "@/pages/Registration/Register/RegisterProgress.vue"
import Step1 from "@/pages/Registration/Register/Step-1"
import Step2 from "@/pages/Registration/Register/Step-2"
import Step3 from "@/pages/Registration/Register/Step-3"
import Login from "@/pages/Registration/Login/Login.vue"
import Recuperar from "@/pages/Login/Recuperar.vue"
import CambiarClave from "@/pages/Login/CambiarClave.vue"
import { redirectDespuesDeEntrar } from "@/Utils/Utils";

export default {
    name: "Register",
    components: {
        Progress,
        Step1,
        Step2,
        Step3,
        Login,
        Recuperar,
        CambiarClave
    },
    data() {
        return {
            step: null,
            logoHorizontal,
            logoMark,
            showWelcomeModal: false,
            welcomeName: ''
        };
    },
    methods: {
        nextStep() {
            if (this.step < 3) {
                this.step++;
                this.$router.push({ path: `/registro/step-${this.step}` });
            } else {
                redirectDespuesDeEntrar(this.$router);
            }
        },
        // Fin del registro: bienvenida y, a los 3 s, a donde iba o a Mi perfil.
        onRegistrationComplete(name) {
            this.welcomeName = name;
            this.showWelcomeModal = true;
            setTimeout(() => {
                this.showWelcomeModal = false;
                document.body.style.overflow = 'auto';
                redirectDespuesDeEntrar(this.$router);
            }, 3000);
        },
        updateStep() {
            this.step = parseInt(this.$route.params.step) || 1;
            if (this.isRegister && !this.$route.params.step) {
                this.$router.replace({ path: '/registro/step-' + this.step });
            }

            // Sin el código de WhatsApp confirmado no se pasa del primer paso,
            // ni escribiendo la URL a mano. Step1 detecta que el usuario ya
            // existe y muestra la verificación en vez del formulario.
            if (this.isRegister && this.step > 1 && this.telefonoSinVerificar()) {
                this.step = 1;
                this.$router.replace({ path: '/registro/step-1' });
            }
        },
        /**
         * ¿Hay una sesión a medio registrar, con el teléfono sin confirmar?
         *
         * Las sesiones anteriores a esta verificación no traen el campo: a esas
         * no se las echa del wizard por algo que en su momento no existía.
         */
        telefonoSinVerificar() {
            try {
                const usuario = JSON.parse(localStorage.getItem('usuario'));
                if (!usuario) return false;
                if (usuario.telefono_verificado === undefined || usuario.telefono_verificado === null) return false;
                return !usuario.telefono_verificado;
            } catch (e) {
                return false;
            }
        }
    },
    computed: {
        isLogin() {
            return this.$route.name === 'login';
        },
        isRegister() {
            return this.$route.name === 'register';
        },
        isRecover() {
            return this.$route.name === 'recuperar';
        },
        isNewPassword() {
            return this.$route.name === 'Cambiar clave';
        },
        message() {
            if (this.isRecover) return this.$t('recover.recover-password');
            if (this.isNewPassword) return this.$t('restore.set-new-password');
            return this.isLogin ? this.$t('login.sign-in') : this.$t('login.register');
        }
    },
    created() {
        this.updateStep();
    },
    watch: {
        $route() {
            this.updateStep();
        }
    }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');

/* ── DESIGN TOKENS ─────────────────────────────────────────── */
.hyf-auth {
  --orange: #FC7010;
  --bg: #FFFFFF;
  --bg-subtle: #FAFAF9;
  --bg-muted: #F4F3F0;
  --bg-deep: #0A0A0B;
  --border: #ECEAE4;
  --border-strong: #DBD9D2;
  --text: #0A0A0B;
  --text-mid: #383840;
  --text-dim: #6E6E76;
  --text-faint: #A0A0A8;
  --hyf-display: 'Geist', sans-serif;
  --hyf-body: 'Geist', sans-serif;
  --hyf-mono: 'Geist Mono', monospace;
  --max-w: 1240px;

  font-family: var(--hyf-body);
  color: var(--text);
  background: transparent; /* fondo de marca del sitio (HyFFondo, en DefaultLayout) */
  font-size: 16px;
  line-height: 1.55;
  letter-spacing: -0.01em;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  /* Estas rutas ocultan el header del sitio (meta.hideHeader), pero
     DefaultLayout igual deja el padding-top de 60 px ($desktop/$mobile-header-height
     en config.scss) y el header de acá quedaba despegado de arriba. */
  margin-top: -60px;
}
.hyf-auth *, .hyf-auth *::before, .hyf-auth *::after { box-sizing: border-box; }

/* ── NAV ────────────────────────────────────────────────────── */
.hyf-auth .auth-nav {
  position: sticky;
  top: 0;
  z-index: 50;
  /* Línea + sombra para que se despegue del fondo (igual que AppHeader). */
  background: rgba(255,255,255,0.94);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  border-bottom: 1px solid rgba(29,26,23,0.12);
  box-shadow: 0 8px 24px -14px rgba(29,26,23,0.28);
}
/* De punta a punta: logo a la izquierda, botón contra la derecha. */
.hyf-auth .auth-nav-inner {
  width: 100%;
  padding: 16px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.hyf-auth .auth-brand {
  display: flex;
  align-items: center;
  text-decoration: none;
}
.hyf-auth .auth-brand-logo { display: block; width: 142px; height: auto; }
.hyf-auth .auth-brand-logo-mobile { display: none; }
.hyf-auth .auth-btn-secondary {
  display: inline-flex;
  align-items: center;
  background: var(--bg-subtle);
  border: 0.5px solid var(--border-strong);
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
  text-decoration: none;
  transition: all 0.15s;
  letter-spacing: -0.01em;
}
.hyf-auth .auth-btn-secondary:hover { background: var(--text); color: #fff; border-color: var(--text); }

/* ── MAIN ───────────────────────────────────────────────────── */
.hyf-auth .auth-main {
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 64px 24px 80px;
  background: transparent;
}
.hyf-auth .auth-container {
  width: 100%;
  max-width: 480px;
}
/* Login / registro: la grilla .bk (bkCuenta.css) maneja anchos y márgenes. */
.hyf-auth .auth-main.auth-main--bk {
  display: block;
  padding: 24px 0 0;
  /* El footer siempre queda abajo, fuera de la primera pantalla: con pasos
     cortos (elegir perfil, el código) y durante el cambio de paso —cuando el
     contenido desaparece un instante— ya no sube. 64 px = alto del header. */
  min-height: calc(100vh - 64px);
}
@media (max-width: 1023px) {
  .hyf-auth .auth-main.auth-main--bk { padding-top: 8px; }
}
.hyf-auth .auth-title {
  font-family: var(--hyf-display);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text);
  margin: 0 0 8px;
  text-align: center;
}

/* ── FOOTER ─────────────────────────────────────────────────── */
.hyf-auth .auth-footer {
  border-top: 0.5px solid var(--border);
  padding: 72px 32px 56px;
  background: var(--bg-subtle);
}
.hyf-auth .auth-footer-inner {
  max-width: var(--max-w);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 48px;
}
.hyf-auth .auth-footer-brand .auth-brand { margin-bottom: 16px; display: flex; }
.hyf-auth .auth-footer-brand .auth-brand-logo { width: 142px; }
.hyf-auth .auth-footer-brand p { font-size: 13px; color: var(--text-dim); line-height: 1.55; max-width: 300px; margin: 0; }
.hyf-auth .auth-footer-col h5 {
  font-family: var(--hyf-mono);
  font-size: 11px;
  color: var(--text-faint);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 0 0 18px;
  font-weight: 500;
}
.hyf-auth .auth-footer-col ul { list-style: none; display: flex; flex-direction: column; gap: 10px; padding: 0; margin: 0; }
.hyf-auth .auth-footer-col a { font-size: 13px; color: var(--text-mid); text-decoration: none; transition: color 0.15s; }
.hyf-auth .auth-footer-col a:hover { color: var(--text); }
.hyf-auth .auth-footer-bottom {
  max-width: var(--max-w);
  margin: 56px auto 0;
  padding-top: 28px;
  border-top: 0.5px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--hyf-mono);
  font-size: 11px;
  color: var(--text-faint);
}
.hyf-auth .auth-footer-legal { display: flex; align-items: center; gap: 8px; }
.hyf-auth .auth-footer-legal a { color: var(--text-faint); text-decoration: none; transition: color 0.15s; }
.hyf-auth .auth-footer-legal a:hover { color: var(--text); }

/* ── FOOTER SOCIAL ICONS ────────────────────────────────────── */
.hyf-auth .auth-footer-social { display: flex; align-items: center; gap: 10px; margin-top: 20px; }
.hyf-auth .auth-footer-social .socialcontainer { height: 40px; overflow: hidden; }
.hyf-auth .auth-footer-social .social-icon-2-2,
.hyf-auth .auth-footer-social .social-icon-2 {
  width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border-radius: 50px;
}
.hyf-auth .auth-footer-social .icon svg { fill: var(--text-dim); }
.hyf-auth .auth-footer-social .social-icon-2-2 { background: transparent; }
.hyf-auth .auth-footer-social .social-icon-2 {
  background: linear-gradient(72.44deg,#ff7a00 11.92%,#ff0169 51.56%,#d300c5 85.69%);
  transition: transform 0.4s cubic-bezier(0.46,-0.78,0.5,1.56);
}
.hyf-auth .auth-footer-social .social-icon-2 svg { opacity: 0; transition: opacity 0.5s 0.2s; }
.hyf-auth .auth-footer-social .socialcontainer:hover .social-icon-2 { transform: translateY(-40px); }
.hyf-auth .auth-footer-social .socialcontainer:hover .social-icon-2 svg { opacity: 1; }

/* ── MOBILE ─────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .hyf-auth .auth-nav-inner { padding: 14px 20px; }
  .hyf-auth .auth-brand-logo-desktop { display: none; }
  .hyf-auth .auth-brand-logo-mobile { display: block; width: 32px; }
  .hyf-auth .auth-footer-inner { grid-template-columns: 1fr 1fr; gap: 32px; }
  .hyf-auth .auth-footer-bottom { flex-direction: column; gap: 12px; text-align: center; }
}
@media (max-width: 480px) {
  .hyf-auth .auth-footer-inner { grid-template-columns: 1fr; }
  .hyf-auth .auth-main { padding: 40px 20px 64px; }
}

/* ── BUTTONS ─────────────────────────────────────────────────── */
.btn-auth-gradient {
  display: block;
  width: 100%;
  padding: 13px 24px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(180deg, #ff8414, #f26b05);
  color: #ffffff;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 16px rgba(232,100,10,0.20);
}
.btn-auth-gradient:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 32px rgba(232,100,10,0.32);
}
.btn-auth-orange {
  display: block;
  width: 100%;
  text-align: center;
  padding: 13px 24px;
  border: none;
  border-radius: 999px;
  background: #FC7010;
  color: #ffffff;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
  box-shadow: 0 4px 14px rgba(252,112,16,0.22);
}
.btn-auth-orange:hover {
  background: #e05e00;
  transform: translateY(-1px);
  box-shadow: 0 10px 28px rgba(252,112,16,0.32);
  color: #ffffff;
}

/* ── TRANSITIONS ─────────────────────────────────────────────── */
/* Login ↔ Registro */
.auth-fade-enter-active, .auth-fade-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}
.auth-fade-enter { opacity: 0; transform: translateY(14px); }
.auth-fade-leave-to { opacity: 0; transform: translateY(-8px); }

/* Paso 1 → 2 → 3 */
.auth-step-enter-active, .auth-step-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.auth-step-enter { opacity: 0; transform: translateX(20px); }
.auth-step-leave-to { opacity: 0; transform: translateX(-20px); }

.auth-view, .auth-step-wrapper { width: 100%; }

/* Entrada inicial de la página.
   Sin `forwards` a propósito: una animación con fill que toca `transform` deja
   al elemento como containing block para siempre, y entonces cualquier hijo con
   position:fixed —un modal a pantalla completa, por ejemplo— se recorta al
   ancho de esta caja en vez de cubrir la ventana. El estado final de los
   keyframes ya es el natural del elemento, así que no hace falta fijarlo. */
.hyf-auth .auth-container {
  animation: auth-appear 0.4s cubic-bezier(0.22,1,0.36,1);
}
@keyframes auth-appear {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: none; }
}

/* ── WELCOME MODAL ──────────────────────────────────────────── */
.welcome-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 11, 0.82);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.welcome-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 52px 52px 44px;
  text-align: center;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.28);
}
.welcome-logo { height: auto; width: 106px; display: block; margin: 0 auto 28px; }
.welcome-title { font-size: 2.2rem; font-weight: 700; color: #0A0A0B; margin: 0; line-height: 1.1; font-family: 'Instrument Serif', Georgia, serif; }
.welcome-comma { color: #FC7010; }
.welcome-name { font-size: 2rem; font-weight: 700; color: #FC7010; margin: 6px 0 22px; font-family: 'Geist', system-ui, sans-serif; }
.welcome-divider { width: 44px; height: 3px; background: #FC7010; border-radius: 2px; margin: 0 auto 22px; }
.welcome-redirect { font-size: 14px; color: #6E6E76; margin: 0 0 18px; }
.welcome-dots { display: flex; gap: 6px; justify-content: center; }
.welcome-dots span { width: 7px; height: 7px; background: #FC7010; border-radius: 50%; animation: dot-bounce 1.2s ease-in-out infinite; }
.welcome-dots span:nth-child(2) { animation-delay: 0.2s; }
.welcome-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes dot-bounce {
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1.1); }
}
.welcome-modal-enter-active { animation: modal-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
.welcome-modal-leave-active { animation: modal-in 0.2s ease-in reverse; }
@keyframes modal-in {
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1); }
}
@media (max-width: 768px) {
  .welcome-card { padding: 40px 28px 36px; }
  .welcome-title { font-size: 1.8rem; }
  .welcome-name  { font-size: 1.6rem; }
}
</style>

<!-- Login y registro: estilos de Mi cuenta + los propios (prefijo bk- / bk-auth-). -->
<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="./bkAuth.css"></style>